import { useCallback, useRef } from "react";

import {
  AnalyticsFieldName,
  SMTAnalyticsSource,
  SMTEntryAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import useAppUserSMTPermissions from "@/hooks/useAppUserSMTPermissions";
import { useAnalytics } from "@/services/Analytics";
import { useAppSelector } from "@/store/hooks";
import {
  selectIsSMTPatientSelectorModalOpen,
  useSMTDispatch,
} from "@/store/reducers/smt";
import SMTPatientSelectorModal from "@/ui/components/smt/SMTPatientSelectorModal";

// Split out SMTPatientSelectorModal and all related logic into its own component here
// to prevent AddSMTEntryFlow from growing unbounded in length and complexity
const AddSMTEntryPatientSelectorModal = () => {
  const { track } = useAnalytics();
  const {
    setAddSMTEntrySelectedPatientId,
    setIsFilePickerModalOpen,
    setIsSelectPatientModalOpen,
  } = useSMTDispatch();
  const isSMTPatientSelectorModalOpen = useAppSelector(
    selectIsSMTPatientSelectorModalOpen,
  );

  const {
    supportPatientsEligibleForSMT: { supportPatientsWithSMTAddAccess },
  } = useAppUserSMTPermissions();

  // Set when a patient is picked so the file picker opens only AFTER this modal has
  // fully dismissed (see onDismissAnimationEnd). Opening the picker on a fixed timer
  // instead can fire before this modal finishes dismissing, leaving the patient
  // selector stacked under the picker.
  const shouldOpenFilePickerOnDismissRef = useRef(false);

  const handleSMTPatientSelectorModalDismiss = useCallback(() => {
    track(SMTEntryAnalyticsEvent.SelectPatientDismissed, {
      [AnalyticsFieldName.Component]: SMTPatientSelectorModal.name,
      [AnalyticsFieldName.Source]: SMTAnalyticsSource.AddSMTEntryFlow,
    });
    setIsSelectPatientModalOpen(false);
  }, [setIsSelectPatientModalOpen, track]);

  const handlePatientSelected = useCallback(
    (patientExternalId: string) => {
      track(SMTEntryAnalyticsEvent.SelectPatientPressed, {
        [AnalyticsFieldName.Component]: SMTPatientSelectorModal.name,
        [AnalyticsFieldName.Source]: SMTAnalyticsSource.AddSMTEntryFlow,
      });
      setAddSMTEntrySelectedPatientId(patientExternalId);
      // Dismiss this modal; the file picker opens from onDismissAnimationEnd once
      // the dismiss has actually completed (no fixed-timer race).
      shouldOpenFilePickerOnDismissRef.current = true;
      setIsSelectPatientModalOpen(false);
    },
    [setIsSelectPatientModalOpen, setAddSMTEntrySelectedPatientId, track],
  );

  const handleDismissAnimationEnd = () => {
    if (!shouldOpenFilePickerOnDismissRef.current) return;
    shouldOpenFilePickerOnDismissRef.current = false;
    // after selecting a patient, a support user proceeds to the same screen
    // that a patient user would've from the start
    setIsFilePickerModalOpen(true);
  };

  return (
    <SMTPatientSelectorModal
      isOpen={isSMTPatientSelectorModalOpen}
      onDismiss={handleSMTPatientSelectorModalDismiss}
      onDismissAnimationEnd={handleDismissAnimationEnd}
      onPatientSelected={handlePatientSelected}
      patients={supportPatientsWithSMTAddAccess}
    />
  );
};

export default AddSMTEntryPatientSelectorModal;
